// GraphQL
import { client } from '../gql/client';
import { selectActionById } from '../gql/selectors/actions';
import { selectAccidentById } from '../gql/selectors/accidents';
import { selectCommunicationById } from '../gql/selectors/communications';
import { FETCH_PREDEFINED_OPTIONS_ACCIDENTS } from '../gql/queries/accidents/predefinedData';
import { FETCH_ACTIVITYS } from '../gql/queries/actions/activitys';
import { FETCH_ASSETS } from '../gql/queries/actions/assets';
import { FETCH_MATERIALS } from '../gql/queries/actions/materials';
import { FETCH_MEASURE_TYPES } from '../gql/queries/actions/measureTypes';
import { FETCH_CODIFICATIONS } from '../gql/queries/actions/codification';
import { FETCH_SUBCODIFICATIONS } from '../gql/queries/actions/subCodification';
import {
  FETCH_AUX_MACHINES,
  FETCH_AUX_MACHINE_TYPES,
} from '../gql/queries/actions/auxMachines';
import { FETCH_ANIMALS } from '../gql/queries/actions/animals';
import { FETCH_VEHICLES, FILTER_VEHICLES } from '../gql/queries/vehicles';
import { GET_VEHICLE_CATEGORIES } from '../gql/queries/vehicleCategory';
import { FETCH_USERS, FETCH_TEAMS } from '../gql/queries/users';
import { GET_BUDGET_CATEGORIES } from '../gql/queries/budget/budgetCategories';
import { GET_BUDGET_ITEMS } from '../gql/queries/budget/budgetItems';
import { GET_SELECTORS_DATA } from '../gql/queries/orders/orders';

// Auth
import { Role } from '../types/auth';
import { decompressSessionStorage } from '../utils/sessionStorage';

/**
 * Precarga de la caché de Apollo para poder OPERAR SIN COBERTURA.
 *
 * Sin red no hay excepciones para nadie: `client.prioritizeCacheValues`
 * convierte todo `network-only` en cache-first y `OfflineCacheFallbackLink`
 * hace de red de seguridad leyendo la caché. Así que lo ÚNICO que decide si
 * una pantalla funciona en un túnel es qué había en la caché normalizada
 * cuando el operario perdió la red — de ahí que haya que calentarla a
 * propósito.
 *
 * Estas tres funciones vivían DUPLICADAS en `WorkOrderDetailed/loader.ts` y
 * `CampaignDetailed/loader.ts` (mismas queries, `prefetchChildrenSummaries`
 * byte a byte). Se traen aquí al aparecer el tercer consumidor —la pestaña
 * Accidentes— porque el modo de fallo de la deriva no se ve: una query que se
 * añade en un loader y no en el otro solo se nota cuando alguien se queda sin
 * datos en mitad del campo. De hecho las dos copias YA divergían en los
 * comentarios (la de Campaign perdió la explicación del gating por rol).
 */

/**
 * Lee el rol activo del usuario desde sessionStorage. Mismo patrón que
 * `rolesAuthRouteCheck.ts` y `loaderIndexPage.ts`. Si falla (sesión rota,
 * ambiente test, etc.) devuelve null y los prefetches role-gated se saltan.
 */
const getCurrentRole = (): Role | null => {
  try {
    const raw = sessionStorage.getItem('user');
    if (!raw) return null;
    const parsed = JSON.parse(decompressSessionStorage(raw));
    return parsed?.user?.rolActive ?? null;
  } catch {
    return null;
  }
};

/**
 * Pre-fetch accident predefined options into Apollo cache.
 * This ensures the data is available offline when the user
 * creates an accident from inside a tunnel without connectivity.
 *
 * Devuelve la promesa (nunca rechaza) para poder await-earla ANTES de
 * `prefetchChildrenSummaries`: los singles por catálogo de la cadena
 * `selectAccidentById` (GetWeatherById, GetLuminosityById, …) resuelven de
 * caché vía los redirects de policies.ts SOLO si este catálogo ya está
 * normalizado — sin el await, la primera carga del detalle corría la carrera
 * y pagaba hasta 6 requests por accidente igualmente (2026-07-21).
 */
export const prefetchAccidentOptions = () =>
  client
    .query({
      query: FETCH_PREDEFINED_OPTIONS_ACCIDENTS,
      fetchPolicy: 'cache-first',
    })
    .catch(() => {});

/**
 * Pre-fetch ALL data needed by the action creation wizard.
 * Each query is non-blocking (fire-and-forget). If any fail, the data
 * simply won't be available offline and the form shows empty dropdowns.
 *
 * Data already cached by MainLayout/selectWorkOrderWithChildren:
 *   capitols, subCapitols, classifications, predefinedOrders,
 *   concessions, roads, teams, externalCompanies, animalAllStatus
 *
 * Data cached here for the wizard steps:
 *   Step 2 (OperatorsAndVehicles): users, vehicles
 *   Step 4 (Edition): activities, assets
 *   Step 5 (Materials): materials, measureTypes, codifications, subCodifications
 *   Step 6 (Machinery): vehicles, auxMachines, auxMachineTypes, vehicleCategories
 *   Step 7 (Animals): animals
 *   Step (Partidas / Budget) — solo si rol Coex/SuperAdmin: budget categories + items.
 *   Tablet/RoomTeam saltan esta tab (BudgetItemsValidationFormPage redirige a
 *   materials), por tanto no necesitan los datos en cache → ahorramos red.
 *
 * NOTA — `GeoPositionRoads` (Step Location) no se prefetchea: Google Maps
 * tiles requieren conectividad para renderizar, por tanto en túnel/offline
 * el mapa está gris y los puntos geo no aportan UX. Si recupera red, la
 * query se ejecuta on-demand desde LocationForm.
 *
 * `GET_SELECTORS_DATA` se añadió al traer el módulo aquí (2026-08-05): lo pide
 * `loadFormOptions` del paso 1 de EditAccident. Los dos detalles ya lo tenían
 * en caché por otras vías; la pestaña Accidentes no.
 */
export const prefetchWizardCatalogs = () => {
  const q = (query: any, variables?: any) =>
    client.query({ query, variables, fetchPolicy: 'cache-first' }).catch(() => {});

  // Step 1 (datos de la OT en los editores)
  q(GET_SELECTORS_DATA);

  // Step 2: Operators & Vehicles
  // FETCH_VEHICLES + FETCH_USERS + FETCH_TEAMS are used by selectActiveOperatorsAndVehicles
  // FILTER_VEHICLES is used by Machinery loader
  q(FETCH_VEHICLES);
  q(FETCH_USERS);
  q(FETCH_TEAMS);
  q(FILTER_VEHICLES, { input: { isActive: true } });

  // Step 4: Edition
  q(FETCH_ACTIVITYS);
  q(FETCH_ASSETS);

  // Step 5: Materials
  q(FETCH_MATERIALS);
  q(FETCH_MEASURE_TYPES);
  q(FETCH_CODIFICATIONS);
  q(FETCH_SUBCODIFICATIONS);

  // Step 6: Machinery
  q(FETCH_AUX_MACHINES);
  q(FETCH_AUX_MACHINE_TYPES);
  q(GET_VEHICLE_CATEGORIES);

  // Step 7: Animals
  q(FETCH_ANIMALS);

  // Step Partidas (Budget) — role-gated: solo Coex y SuperAdmin pueden
  // gestionar partidas. Tablet salta a materials, RoomTeam ve readonly y
  // tampoco las edita en flujos offline.
  const role = getCurrentRole();
  if (role === Role.SuperAdmin || role === Role.Coex) {
    q(GET_BUDGET_CATEGORIES);
    q(GET_BUDGET_ITEMS, { filters: { isActive: true }, pagination: null });
  }
};

/**
 * Pre-fetch de la cadena completa de cada hijo (Action/Accident/Communication)
 * que cuelga de la OT actual. Ejecuta los selectores enteros (`selectXById`)
 * para que el cache de Apollo quede poblado con TODAS las subqueries
 * (concession, capitol, road, geoPositionRoads, geoPositionAction, etc.).
 *
 * El loader la await-ea para garantizar que al renderizar la pantalla del OT,
 * el cache contenga TODOS los datos necesarios. Sin esto, si el operario quita
 * la red antes de que las cadenas más largas (Accident, ~22 queries) terminen,
 * el cache queda parcial y el detalle offline falla.
 *
 * Errores por chain se silencian con `.catch(() => undefined)` para que un
 * fallo aislado no aborte el resto del `Promise.all`.
 *
 * Coste: el `defer` se retrasa ~1-3 segundos en OT/Campaña con muchos hijos
 * (típico 2s para una OT de 10 hijos). Aceptable porque garantiza offline.
 *
 * Detalle en `docs/superpowers/plans/2026-05-12-offline-edit-action-accident.md` § Fase 2.
 */
export const prefetchChildrenSummaries = async (
  workOrder: unknown
): Promise<void> => {
  if (!Array.isArray(workOrder)) return;
  type Child = {
    type?: string;
    actionID?: string;
    accidentID?: string;
    communicationID?: string;
  };
  const items = workOrder as Child[];

  const promises: Promise<unknown>[] = [];
  items.forEach((item) => {
    switch (item.type) {
      case 'action':
        // preferCache: la Action y su Order de cada hija acaban de bajar por
        // red en `selectWorkOrderWithChildren` (mismo documento y variables)
        // — repetirlos network-only aquí duplicaba el N+1 (getActionById×A).
        if (item.actionID)
          promises.push(
            selectActionById(item.actionID, { preferCache: true }).catch(
              () => undefined
            )
          );
        break;
      case 'accident':
        if (item.accidentID)
          promises.push(
            selectAccidentById(item.accidentID).catch(() => undefined)
          );
        break;
      case 'communication':
        if (item.communicationID)
          promises.push(
            selectCommunicationById(item.communicationID).catch(() => undefined)
          );
        break;
    }
  });

  await Promise.all(promises);
};

/**
 * Precarga la cadena de cada accidente LISTADO en la pestaña Accidentes, para
 * que su editor abra completo sin cobertura (2026-08-05).
 *
 * EN SERIE a propósito (`for…of` con `await` dentro, jamás `Promise.all`): el
 * techo de concurrencia debe ser 1 pase el tamaño de página que pase. En
 * paralelo, una página de 25 filas dispararía decenas de peticiones a la vez
 * desde una tablet — eso no es "segundo plano", es competir con lo que el
 * usuario está haciendo.
 *
 * El coste incremental REAL es bajo y está medido en el spec: de las 7 queries
 * de `selectAccidentById`, 4 son catálogos compartidos que se cachean una sola
 * vez para toda la tabla y `GET_ORDER_BY_ID` ya la trae el `selectAccidentOrders`
 * del propio loader (batcheada). Quedan ~2 por fila.
 *
 * Llamar SIEMPRE detrás de `prefetchAccidentOptions` + `prefetchWizardCatalogs`:
 * con los catálogos ya normalizados, cada cadena resuelve los suyos por
 * redirect de caché en vez de ir a red.
 */
export const prefetchAccidentRows = async (
  rows: Array<{ accidentID?: string }> | undefined | null
): Promise<void> => {
  if (!Array.isArray(rows)) return;
  for (const row of rows) {
    if (!row?.accidentID) continue;
    await selectAccidentById(row.accidentID).catch(() => undefined);
  }
};
